/**
 * AlphaScout ActionabilityBadge — Phase A (Session 142c)
 * =======================================================
 *
 * Tells the user whether a pick is still actionable:
 *
 *   🟢 FRESH              — enter at market
 *   🟡 ENTER_ON_PULLBACK  — wait for a pullback toward entry
 *   🔴 TOO_LATE           — skip this one, R:R is broken
 *   ⚪ NO_POSITION         — lifecycle row hasn't caught up yet
 *
 * The label + reason are both computed server-side in
 * `backend/alphascout/actionability.py`. This component is pure
 * presentation — no classification logic lives here, ensuring the
 * backend tests are the single source of truth for badge semantics.
 */
import React from 'react';
import { Zap, Clock, XCircle, HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const STYLES = {
  FRESH: {
    className: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/40',
    icon: Zap,
    dot: 'bg-emerald-400',
    short: 'FRESH',
  },
  ENTER_ON_PULLBACK: {
    className: 'text-amber-300 bg-amber-500/10 border-amber-500/40',
    icon: Clock,
    dot: 'bg-amber-300',
    short: 'PULLBACK',
  },
  TOO_LATE: {
    className: 'text-rose-400 bg-rose-500/10 border-rose-500/40',
    icon: XCircle,
    dot: 'bg-rose-400',
    short: 'TOO LATE',
  },
  NO_POSITION: {
    className: 'text-gray-400 bg-gray-500/10 border-gray-500/40',
    icon: HelpCircle,
    dot: 'bg-gray-400',
    short: '—',
  },
};

const DEFAULT_STYLE = STYLES.NO_POSITION;

const ActionabilityBadge = ({ actionability, compact = true }) => {
  if (!actionability) {
    return null;
  }

  const style = STYLES[actionability.label] || DEFAULT_STYLE;
  const Icon = style.icon;

  const days = actionability.days_since_added ?? 0;
  const move = actionability.move_since_add_pct;
  const hasMove = move !== null && move !== undefined;

  // Tooltip content — summarises the three facts the user needs:
  // (1) why we classified it this way (reason), (2) how old the pick
  // is in days, (3) current delta from add-date entry.
  const tooltip = (
    <div
      className="max-w-[260px] space-y-1.5 text-xs"
      data-testid={`actionability-tooltip-${actionability.label}`}
    >
      <div className="flex items-center gap-1.5 font-semibold">
        <span className={`inline-block h-2 w-2 rounded-full ${style.dot}`} />
        {style.short}
      </div>
      <div className="text-muted-foreground leading-snug">
        {actionability.reason}
      </div>
      <div className="flex items-center justify-between gap-3 text-[10px] pt-1 border-t border-border/40">
        <span className="font-mono">{days}d held</span>
        {hasMove && (
          <span
            className={`font-mono ${
              move > 0 ? 'text-emerald-400' : move < 0 ? 'text-rose-400' : 'text-muted-foreground'
            }`}
          >
            {move > 0 ? '+' : ''}
            {move.toFixed(2)}%
          </span>
        )}
      </div>
    </div>
  );

  const badge = (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold whitespace-nowrap ${style.className}`}
      data-testid={`actionability-badge-${actionability.label}`}
    >
      <Icon className="w-3 h-3" />
      {compact ? style.short : actionability.label.replace(/_/g, ' ')}
    </span>
  );

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="left" className="p-2">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ActionabilityBadge;
